import { NavLink, useLocation } from 'react-router-dom';
import { useFeatureFlags } from '../hooks/useFeatureFlags.jsx';
import { X } from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from './ui/sidebar';
import {
  LayoutDashboard,
  Bus,
  Route,
  MapPin,
  Navigation,
  BellRing,
  ShieldCheck,
  GraduationCap,
  User,
  BusFront,
  Mail,
  Gauge,
  Terminal,
  AlertTriangle,
} from 'lucide-react';

const IS_DEV_MODE =
  import.meta.env.VITE_DEV_MODE === 'false' ? false : import.meta.env.VITE_DEV_MODE === 'true' || import.meta.env.DEV;

const navGroups = [
  {
    label: 'Suivi GPS',
    items: [
      { title: 'Suivi GPS', icon: Navigation, path: '/admin/tracking', page: 'gps-tracking' },
      { title: 'Flotte en temps réel', icon: Gauge, path: '/admin/fleet', page: 'gps-tracking' },
    ],
  },
  {
    label: 'Gestion',
    items: [
      { title: 'Élèves', icon: GraduationCap, path: '/admin/students', page: 'students' },
      { title: 'Parents', icon: User, path: '/admin/parents', page: 'parents' },
      { title: 'Chauffeurs', icon: BusFront, path: '/admin/drivers', page: 'drivers' },
      { title: 'Bus', icon: Bus, path: '/admin/buses', page: 'buses' },
      { title: 'Arrêts', icon: MapPin, path: '/admin/stops', page: 'stops' },
      { title: 'Assignations', icon: Route, path: '/admin/assignments', page: 'assignments' },
    ],
  },
  {
    label: 'Analytics',
    items: [
      { title: 'Tableau de bord', icon: LayoutDashboard, path: '/admin/dashboard', page: 'dashboard' },
      { title: 'Notifications', icon: BellRing, path: '/admin/notifications', page: 'notifications' },
      { title: 'Demandes', icon: Mail, path: '/admin/requests', page: 'requests' },
      { title: 'Incidents', icon: AlertTriangle, path: '/admin/incidents', page: 'incidents' },
    ],
  },
];

const AppSidebar = () => {
  const location = useLocation();
  const { isPageEnabled, isLoaded } = useFeatureFlags();
  const { isMobile, setOpenMobile } = useSidebar();

  const closeOnMobile = () => {
    if (isMobile) setOpenMobile(false);
  };

  return (
    <Sidebar collapsible="icon">
      {/* ── Header : logo + bouton toggle/fermeture ── */}
      <SidebarHeader className="border-b border-sidebar-border h-16 justify-center overflow-hidden">
        <div className="flex items-center justify-between group-data-[collapsible=icon]:justify-center px-1 min-w-0">
          {/* Logo + texte — masqués en mode icône desktop */}
          <div className="flex items-center gap-2 min-w-0 group-data-[collapsible=icon]:hidden">
            <img src="/logo.png" alt="M'EyeBus" className="h-8 w-8 shrink-0 object-contain" />
            <div className="flex flex-col overflow-hidden">
              <img src="/logo_trademark.png" alt="M'EyeBus" className="h-5 w-auto object-contain" />
              <p className="text-xs text-sidebar-foreground/50 leading-tight whitespace-nowrap">Admin Dashboard</p>
            </div>
          </div>

          {/* Mobile : bouton X fermeture | Desktop : toggle collapse */}
          {isMobile ? (
            <button
              onClick={() => setOpenMobile(false)}
              className="p-1.5 rounded-lg text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors shrink-0"
              aria-label="Fermer le menu"
            >
              <X className="h-5 w-5" />
            </button>
          ) : (
            <SidebarTrigger className="shrink-0 text-sidebar-foreground/70 hover:text-sidebar-foreground" />
          )}
        </div>
      </SidebarHeader>

      {/* ── Navigation ── */}
      <SidebarContent>
        {navGroups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const enabled = isLoaded
                    ? isPageEnabled(item.page)
                    : ['students', 'parents', 'drivers', 'gps-tracking', 'notifications'].includes(item.page);
                  const active = location.pathname === item.path;
                  const Icon = item.icon;

                  if (!enabled) {
                    return (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton
                          tooltip={item.title}
                          className="opacity-50 cursor-not-allowed hover:bg-transparent hover:text-current"
                        >
                          <Icon />
                          <span>{item.title}</span>
                          <ShieldCheck className="ml-auto h-3.5 w-3.5 text-orange-500 shrink-0" />
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  }

                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton asChild isActive={active} tooltip={item.title}>
                        <NavLink to={item.path} onClick={closeOnMobile}>
                          <Icon />
                          <span>{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}

        {IS_DEV_MODE && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-orange-500">Développement</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === '/admin/dev/simulator'}
                    tooltip="Simulateur GPS"
                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                  >
                    <NavLink to="/admin/dev/simulator" onClick={closeOnMobile}>
                      <Terminal />
                      <span>Simulateur GPS</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <div className="px-3 py-2 group-data-[collapsible=icon]:hidden">
          <p className="text-xs text-sidebar-foreground/40">v{__APP_VERSION__}</p>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
